/**
 * useAdminUsers Hook
 *
 * Fetches and manages admin user management data.
 * Provides listing, detail, role updates, status changes, and soft deletion.
 *
 * Story: SCRUM-841 — FIX-H04: Admin user management
 */

import { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';

/**
 * Hook for admin user listing with filters, search, and pagination.
 *
 * @param {Object} initialFilters - Initial filter values
 * @returns {Object} Users data, loading state, error, and actions
 */
export function useAdminUsers(initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [stats, setStats] = useState({ byStatus: {}, byRole: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.role) { params.set('role', filters.role); }
      if (filters.status) { params.set('status', filters.status); }
      if (filters.user_type) { params.set('user_type', filters.user_type); }
      if (filters.email_verified !== undefined && filters.email_verified !== '') {
        params.set('email_verified', String(filters.email_verified));
      }
      if (filters.search) { params.set('search', filters.search); }
      if (filters.page) { params.set('page', String(filters.page)); }
      if (filters.limit) { params.set('limit', String(filters.limit)); }
      if (filters.sortBy) { params.set('sortBy', filters.sortBy); }
      if (filters.sortOrder) { params.set('sortOrder', filters.sortOrder); }

      const queryString = params.toString();
      const url = `/api/v1/admin/users${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest(url);
      if (response.success) {
        setUsers(response.data || []);
        setPagination(response.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
        setStats(response.stats || { byStatus: {}, byRole: {} });
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: newFilters.page || 1 }));
  }, []);

  const refresh = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    pagination,
    stats,
    loading,
    error,
    filters,
    updateFilters,
    refresh
  };
}

/**
 * Hook for admin user detail view.
 *
 * @param {string} userId - User UUID to fetch
 * @returns {Object} User detail, loading state, error, and actions
 */
export function useAdminUserDetail(userId) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUser = useCallback(async () => {
    if (!userId) { return; }
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(`/api/v1/admin/users/${userId}`);
      if (response.success) {
        setUser(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const updateRoles = useCallback(async (roles, reason) => {
    const response = await apiRequest(`/api/v1/admin/users/${userId}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roles, reason })
    });
    if (response.success) {
      setUser(response.data);
    }
    return response;
  }, [userId]);

  const updateStatus = useCallback(async (status, reason) => {
    const response = await apiRequest(`/api/v1/admin/users/${userId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, reason })
    });
    if (response.success) {
      setUser(response.data);
    }
    return response;
  }, [userId]);

  const deleteUser = useCallback(async (reason) => {
    const response = await apiRequest(`/api/v1/admin/users/${userId}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason })
    });
    return response;
  }, [userId]);

  return {
    user,
    loading,
    error,
    refresh: fetchUser,
    updateRoles,
    updateStatus,
    deleteUser
  };
}

/**
 * SCRUM-1806: Fetches the role implication hierarchy from the API so the
 * super-admin role-management UI can render the cascade tree and compute
 * cascade previews client-side without duplicating precedence logic.
 *
 * @returns {Object} hierarchy: { version, roles: { name: { implies: string[] } } }
 */
export function useRoleHierarchy() {
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiRequest('/api/v1/admin/users/_role-hierarchy');
        if (!cancelled && response.success) {
          setHierarchy(response.data);
        }
      } catch (err) {
        if (!cancelled) { setError(err.message || 'Failed to load role hierarchy'); }
      } finally {
        if (!cancelled) { setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { hierarchy, loading, error };
}

/**
 * SCRUM-1806: Pure helper — given a role and the hierarchy, returns the
 * transitive set of roles implied by the given role (the role itself is
 * NOT included). Used for client-side cascade previews.
 *
 * @param {string} role
 * @param {Object} hierarchy - shape returned by useRoleHierarchy
 * @returns {string[]}
 */
export function getImpliedRoles(role, hierarchy) {
  if (!hierarchy || !hierarchy.roles || !hierarchy.roles[role]) { return []; }
  const visited = new Set();
  function collect(r) {
    if (visited.has(r)) { return; }
    visited.add(r);
    const implies = hierarchy.roles[r] ? hierarchy.roles[r].implies : [];
    for (const implied of implies) {
      collect(implied);
    }
  }
  collect(role);
  visited.delete(role);
  return [...visited];
}

export default useAdminUsers;
